<template>
  <div>
    <div class="checkout-confirm-action">
      <div v-if="!signupNewAccount && !clientModel.impersonating">
        <input
          id="approve-terms"
          v-model="approveTerms"
          data-pw="accept-terms"
          type="checkbox"
          class="checkout-confirm-action__checkbox">

        <label
          for="approve-terms"
          class="checkout-confirm-action__label"
          v-html="t('approveTermsInformation')"></label>
      </div>

      <div class="checkout-confirm-action__button">
        <oie-base-button
          v-element-loader="{ endpoint: ['auth/checkout/create', 'checkout/submitOrder'] }"
          css-classes="button--with-border button--green"
          :text="isOrderRequestVisitorState ? t('submitOrderRequestButtonLabel') : t('submitOrderButtonLabel')"
          :disabled="orderSent || (!approveTerms && !clientModel.impersonating)"
          button-type="button"
          data-pw="checkout-submit"
          @click="submitOrderData">
        </oie-base-button>
        <div class="checkout-confirm-note">
          <oie-use-svg svg="circle-error-light"></oie-use-svg>
          <p>
            {{ isOrderRequestVisitorState ? t('submitOrderRequestNote') : t('submitOrderNote') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import httpService from '@/core/http-service'
import _ from 'lodash'
import { mapGetters, mapActions } from 'vuex'
import gtm from '@/core/enhanced-ecommerce-gtm'

export default {
  props: {
    acceptConditions: {
      type: Boolean,
      default: false
    },
    checkoutPayload: {
      type: Object,
      default: null
    },
    customer: {
      type: Object,
      default: null
    }
  },
  data: () => ({
    approveTerms: false,
    orderSent: false,
    signupNewAccount: false
  }),
  computed: {
    ...mapGetters('checkout', ['isOrderRequestVisitorState']),
    ...mapGetters('chat', { chatUsername: 'username', chatSessionId: 'sessionId' })
  },
  watch: {
    approveTerms(value) {
      this.publish('checkout-approve-terms', value)
    }
  },
  mounted() {
    if ((this.clientModel.isAuthenticated && !this.clientModel.impersonating) || this.clientModel.impersonating) {
      this.approveTerms = true // Existing customers already agreed to terms
    } else {
      this.approveTerms = !!this.checkoutPayload.approveTerms
    }

    this.subscribe('checkout-container-signup', (value) => {
      this.signupNewAccount = value.toggleUserPassword || false
      if (this.signupNewAccount) {
        this.approveTerms = value.approveTerms
      }
    })
  },
  methods: {
    ...mapActions('chat', ['setUsername']),
    async submitOrderData() {
      let isSignUpValid = false
      let isInvoiceOrderNoValid = false
      let isNotificationFormValid = false
      let isPdfInvoiceFormValid = false
      let isPdfReceiptFormValid = false
      let idNumberFormIsValid = false
      let superUserFormIsValid = false

      const checkoutSignUpForm = this.validatorFor('checkoutSignUpForm')
      if (checkoutSignUpForm) {
        isSignUpValid = await checkoutSignUpForm.validate()
      }

      const invoiceOrderNoForm = this.$parent.$refs.invoiceOrderNoForm
      if (invoiceOrderNoForm) {
        isInvoiceOrderNoValid = await invoiceOrderNoForm.validate()
      }

      const notificationForm = this.validatorFor('notificationForm')
      if (notificationForm) {
        isNotificationFormValid = await notificationForm.validate()
      }

      const pdfInvoiceForm = this.validatorFor('pdfInvoiceForm')
      if (pdfInvoiceForm) {
        isPdfInvoiceFormValid = await pdfInvoiceForm.validate()
      }

      const pdfReceiptForm = this.validatorFor('pdfReceiptForm')
      if (pdfReceiptForm) {
        isPdfReceiptFormValid = await pdfReceiptForm.validate()
      }

      const idNumberForm = this.validatorFor('idNumberForm')
      if (idNumberForm) {
        idNumberFormIsValid = await idNumberForm.validate()
      }

      const superUserForm = this.validatorFor('superUserForm')
      if (superUserForm) {
        superUserFormIsValid = await superUserForm.validate()
      }

      if ((!idNumberFormIsValid && idNumberForm) ||
        (!isPdfInvoiceFormValid && pdfInvoiceForm) ||
        (!isPdfReceiptFormValid && pdfReceiptForm) ||
        (!isSignUpValid && checkoutSignUpForm) ||
        (!isInvoiceOrderNoValid && invoiceOrderNoForm) ||
        (!isNotificationFormValid && notificationForm) ||
        (!superUserFormIsValid && superUserForm)) {
        let selector = !isSignUpValid && checkoutSignUpForm ? '.checkout-sign-up' : null
        selector = !isInvoiceOrderNoValid && invoiceOrderNoForm ? '.invoice-order-no-form' : selector
        selector = !isNotificationFormValid && notificationForm ? '.notification-form' : selector
        selector = !isPdfInvoiceFormValid && pdfInvoiceForm ? '.pdfInvoiceForm' : selector
        selector = !isPdfReceiptFormValid && pdfReceiptForm ? '.pdfReceiptForm' : selector
        selector = !idNumberFormIsValid && idNumberForm ? '.idNumberForm' : selector
        selector = !superUserFormIsValid && superUserForm ? '.overrideConfEmail' : selector

        const firstErrorElement = document.querySelector(selector).querySelector('.input--has-errors')
        const defaultFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize)
        const elementPosition = this.firstErrorScrollPosition(firstErrorElement) - (4 * defaultFontSize)

        window.scroll({
          top: elementPosition,
          left: 0,
          behavior: 'smooth'
        })

        return
      }

      var payload = JSON.parse(JSON.stringify(this.checkoutPayload))
      payload.pdfInvoiceEmail = this.customer && this.customer.pdfInvoiceEmail

      if (this.customer) {
        payload.pdfInvoice = this.customer && this.customer.pdfInvoice
      } else {
        payload.pdfInvoice = false
      }

      payload.pdfReceiptEmail = this.customer && this.customer.pdfReceiptEmail

      if (this.customer) {
        payload.pdfReceipt = this.customer && this.customer.pdfReceipt
      } else {
        payload.pdfReceipt = false
      }

      if (this.clientModel.isAuthenticated) {
        if (this.customer) {
          payload.customerNumber = this.customer.customerNumber
        }
      } else {
        payload.customerNumber = 'anonymous'
      }

      if (this.clientModel.isAuthenticated) {
        payload.deliveryAddress.customerName = payload.deliveryAddress.name
      } else if (!this.clientModel.isAuthenticated && !payload.deliveryAddress.isCompanyCustomer) {
        payload.deliveryAddress.customerName = payload.deliveryAddress.firstName.trim() + ' ' + payload.deliveryAddress.lastName.trim()
      }

      var validCheck
      var warnMessage
      await httpService.post('checkout/validateOrder', payload).then((response) => {
        if (!response.data.success) {
          validCheck = false
          warnMessage = response.data.message
        }else {
          validCheck = true
        }
      })

      if(!validCheck) {
        await new Promise((resolve, reject) => {
          this.triggerModal(warnMessage, resolve, reject)
        })
      }

      if ((payload && payload.deliveryAddress && !payload.deliveryAddress.toggleUserPassword) || this.clientModel.isAuthenticated) {
        httpService.post('checkout/submitOrder', payload).then((response) => {
          this.orderSent = true
          window.location = response.data.redirectUrl
        })
      } else {
        payload.authToken = true

        // Add chatSessionId if username differs from chat username to patch the chat session data
        const username = payload.deliveryAddress && payload.deliveryAddress.username
        if (this.chatSessionId && username !== this.chatUsername) {
          payload.chatSessionId = this.chatSessionId
        }

        httpService.post('checkout/validateOrder', payload).then(() => {
          httpService.post('auth/checkout/create', payload).then(() => {
            const isCompanyCustomer = payload.deliveryAddress.isCompanyCustomer
            // update new user details in the anonymous chat session
            if (this.chatSessionId && username !== this.chatUsername) {
              this.setUsername(username)
            }

            gtm.addSignUpEvent(isCompanyCustomer === null ? false : isCompanyCustomer)
            httpService.post('checkout/updatestatus', payload).then(() => {
              httpService.post('checkout/submitOrder', payload).then((response) => {
                this.orderSent = true
                window.location = response.data.redirectUrl
              })
            })
          })
        })
      }
    },
    triggerModal(message, resolve, reject) {
      this.publish('modal.show.generic', {
        header: this.tStatic('superUserWarningMessageHeader'),
        text: message,
        buttons: [ {
          text: this.tStatic('superUserContinueButton'),
          usePrimaryButton: true,
          handler: () => {
            this.publish('modal.hide.generic')
            resolve()
          }
        }]
      })
    },
    firstErrorScrollPosition(element) {
      const rect = element.getBoundingClientRect()
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop

      return (rect.top + scrollTop) - 30
    },
    validatorFor(refName) {
      const refs = _.filter(this.$parent.$children, child => child.$refs && child.$refs[refName])
      if (refs && refs.length > 0) {
        return refs[0].$refs[refName]
      } else {
        return null
      }
    }
  }

}
</script>

<style lang="scss">
.checkout-confirm-action {
  display: flex;
  flex-direction: column;

  @include media(small) {
    flex-direction: row;
    justify-content: space-between;
  }
}

.checkout-confirm-action__checkbox {
  opacity: 0;
  position: absolute;
  &:focus + label:before {
    outline: 1px solid color(black);
    outline-offset: 2px;
  }
  &:focus:not(:focus-visible) + label:before { outline: none; }
}

.checkout-confirm-action__label {
  @include caption;
  position: relative;
  display: flex;
  align-items: center;
  max-width: 35rem;
  margin-bottom: measure(half);

  &:hover {
    cursor: pointer;
  }

  @include media(small) {
    @include body-smaller;
    align-items: center;
    margin-right: measure(half);
    margin-bottom: 0;
  }

  &:before {
    content: '';
    flex-shrink: 0;
    border: 2px solid color(border-blue-2);
    width: 1.875rem;
    height: 1.875rem;
    margin-right: measure(half);
  }

  &:after {
    content: '' ;
    position: absolute;
    top: 50%;
    left: .4rem;
    transform: translateY(-50%) translateX(-2rem);
    background-image: url("data:image/svg+xml,%3Csvg width='30' height='28' viewBox='0 0 30 28' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11.034 27.39L0 16.842l3.3-3.424 7.27 6.95L26.279.8 30 3.764z' fill='%2344B1FA' fill-rule='evenodd'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-size: contain;
    width: 1.1rem;
    height: 1.1rem;
    opacity: 0;
    transition: transform 120ms linear, opacity 120ms linear;
  }
}

.checkout-confirm-action__checkbox:checked + .checkout-confirm-action__label {
  &:after {
    transform: translateY(-50%) translateX(0);
    opacity: 1;
  }
}

.checkout-confirm-action__button {

  @include media(small) {
    max-width: 360px;
  }

  button {
    margin-left: auto;
    width: 100%;

    @media print {
      display: none;
    }
  }
}

.checkout-confirm-note {
  @include body;
  display: flex;
  gap: 0.5rem;
  background-color: #f1faf6;

  padding: 1rem;

  svg {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    margin-top: 2px;
  }
}

.checkout-sign-up .superuser-terms-reminder {
  background-color: #ffa;
  border: blue 1px solid;
  text-align: center;
  padding: measure(quarter);
}
</style>
